import { alpha, darken } from "@mui/material/styles"

// DEBUG only feature flag
export const DEBUG_FEATURES = `${window.__RUNTIME_CONFIG__.REACT_APP_DEBUG_FEATURES}`.toLowerCase() === "true"

// Synthetic custom-report id for the auto-generated meeting transcript report
// (kept in sync with the backend MEETING_TRANSCRIPT_REPORT_ID).
export const MEETING_TRANSCRIPT_REPORT_ID = "meeting_transcript"
// (kept in sync with the backend GAP_ANALYSIS_REPORT_ID).
export const GAP_ANALYSIS_REPORT_ID = "gap_analysis"
// Auto Runbook / IR Plan report (guided sessions). Kept in sync with backend RUNBOOK_REPORT_ID.
export const RUNBOOK_REPORT_ID = "runbook"

// Scenario builder feature
const BUILDER_FEATURE_URL_RAW = `${window.__RUNTIME_CONFIG__.REACT_APP_BUILDER_URL}`
export const BUILDER_FEATURE_URL = BUILDER_FEATURE_URL_RAW.toLowerCase() === "none" ? "" : BUILDER_FEATURE_URL_RAW
export const BUILDER_FEATURE_IS_EXTERNAL = /^(http|https):\/\/(.*)$/.test(BUILDER_FEATURE_URL.toLowerCase())

// In-product help video hosting base (e.g. an S3/CDN origin). Videos are expected at
// `${HELP_VIDEO_BASE}/help/<routeKey>.mp4`. Left empty ⇒ text-only help (no video slot).
// Sourced from runtime config so the storage location is never hardcoded in source.
const HELP_VIDEO_BASE_RAW = `${window.__RUNTIME_CONFIG__?.REACT_APP_HELP_VIDEO_BASE ?? ""}`
const HELP_VIDEO_BASE_OVERRIDE = ["", "undefined", "none", "null"].includes(HELP_VIDEO_BASE_RAW.toLowerCase()) ? "" : HELP_VIDEO_BASE_RAW
// Default to the app's own public base (videos are bundled in public/help and ship with the
// client image), so help videos are durable across dev/ui-demo/demo/prod with ZERO per-env
// config. Set REACT_APP_HELP_VIDEO_BASE at runtime only to override with an external CDN/S3.
export const HELP_VIDEO_BASE = HELP_VIDEO_BASE_OVERRIDE || `${process.env.PUBLIC_URL ?? ""}`

export const GAME_MODES = {
  VIEW_ONLY: "view_only",
  GAME_PLAY: "game_play",
  GAME_OVER: "game_over",
}

export const ROUTE_PATHS = {
  ROOT: "/",
  LOGIN: "/login",
  DASHBOARD: "/dashboard",
  DASHBOARD_DETAILS: "/dashboard/:gameSessionId/details",
  GROUP_DASHBOARD: "/group-dashboard",
  GROUP_DASHBOARD_DETAILS: "/group-dashboard/:groupName/details",
  DETAILS: {
    ROOT: "details",
    // Child routes should not start with '/'
    AVERAGE_SIMULATION_TIME: "ast",
    RECOMMENDATIONS: "recommendations",
    RECOMMENDATION_ACTION_ROOT: "action",
    RECOMMENDATION_ACTION: "action/:actionItemId",
    ACTIONS_LIST: "actions",
    ACTIONS_LIST_WITH_TRENDS: "actions-with-trends",
    INDIVIDUAL_PERFORMANCE: "individual-performance",
    INDIVIDUAL_PERFORMANCE_WITH_TRENDS: "individual-performance-with-trends",
  },
  REPORT: "/report",
  // KPI P2 §13.3-13.4, §13.8
  PEOPLE: "/people",
  MY_PROGRESS: "/my-progress",
  // "Compare groups" was renamed "Compare departments" (2026-10-28); the old path redirects.
  COMPARE_DEPARTMENTS: "/compare-departments",
  COMPARE_GROUPS: "/compare-groups",
  OLD_REPORT: "/report-old",
  GROUP_GAME_REPORT: "/group-game-report",
  // CTF reports spec §2/§3: the CTF player report and the staff event report.
  CTF_REPORT: "/ctf-report",
  CTF_EVENT_REPORT: "/ctf-event-report",

  // Company Paths
  COMPANY_ROOT: "/company",
  COMPANY_HOME: "/company/home",
  COMPANY_DETAILS: "/company/details",
  COMPANY_EMAIL_SETUP: "/company/email-setup",

  // Game Paths
  GAME_ROOT: "/games",
  GAME_ADD_NEW: "/game/add-game",
  GAME_EDIT: "/game/edit",
  GAME_RESTART: "/game/restart",
  GAME_DUPLICATE: "/game/duplicate",
  GAME_PLAYBOARD: "/game/playboard",
  GAME_PLAYBOARD_VIEW_ONLY: `/game/playboard?${GAME_MODES.VIEW_ONLY}=1`,
  GAME_FACILITATOR_VIEW: "/games/:gameSessionId/facilitator",
  // Live CTF host console (?view=present → projector view). :gameSessionId = group or member name.
  GAME_CTF_HOST: "/games/:gameSessionId/ctf-host",
  // Sub-path for the new full-screen player experience. Opt-in per session
  // via the `useNewGameplayExperience` flag on the session document.
  GAME_NEW_EXPERIENCE_SUFFIX: "/new-experience",

  // Scenario Builder
  BUILDER_NEW: "/builder/new",
  SCENARIO: "/scenario",
  SCENARIO_DETAILS: {
    ROOT: "dashboard",
    GENERATOR: "generator",
    CUSTOMIZE: "customize",
    CUSTOMIZER: "customizer",
    PROMPTS: "prompts",
    PROMPT_CONFIG: "prompt-config",
    TEMPLATES: "templates",
    GENERATED_SCENARIOS: "scenarios-list",
    UPLOAD_TEMPLATE: "upload-template",
  },

  // Full-screen scenario builder (renders outside AppLayout — no app chrome).
  // Keep in sync with BUILDER_BASE_PATH in src/builder/fullscreen/BuilderShell.jsx.
  SCENARIO_BUILD: "/scenario/build",

  // Scenario Listings and Customization
  SCENARIOS: "/scenarios",
  SCENARIO_CUSTOMIZATION: (scenarioId) => `/scenario/customizer/${scenarioId}/scenario-details`,
  SCENARIO_EDIT: (simulationId) => `/scenario/edit/${simulationId}`,

  // Integration Hub; /integrations/<tool> is one tool's page (the same route and screen).
  INTEGRATIONS: "/integrations",
  INTEGRATIONS_ROUTE: "/integrations/:toolId?",

  // Partner org / child-customer management
  ORGANIZATIONS: "/organizations",

  // Help Center — task-organized help hub + step walkthroughs (all signed-in roles)
  HELP: "/help",
  HELP_TOPIC: "/help/:topicId",
  // Settings Wrapper
  SETTINGS: "/settings",
  // Manager settings: Update Prompts + Prompt Config under one tabbed page.
  // Active tab is selected via the `?tab=` query param (see SETTINGS_TABS).
  SETTINGS_PROMPT_PROFILES: "/settings/prompt-profiles",
  EVALUATIONS: "/settings/evaluations",
  EVALUATION_RUN: "/settings/evaluations/:runId",
  ADMIN_SETTINGS: "/admin/settings",
  COMPANY_SETTINGS: "/company/settings",
  COMPANY_USER_MANAGEMENT: "/company/users",

  // Security Settings (Enterprise Auth)
  SECURITY_SETTINGS: "/settings/security",
  SECURITY_SSO: "/settings/security/sso",
  SECURITY_MFA: "/settings/security/mfa",

  // Incidents
  INCIDENTS: "/incidents",
  INCIDENT_NEW: "/incidents/new",
  // Invite-link landing page — registered as a static path (outside AppLayout,
  // like /invite) so it can be allow-listed verbatim in Auth0 returnTo settings.
  // The token travels in the ?token= query string.
  INCIDENT_INVITE: "/incidents/invite",
  // Set-password landing page for an invited administrator. Static path (outside
  // AppLayout) — the invitee has no session yet. Token travels in ?token=.
  SET_PASSWORD: "/set-password",
  FORGOT_PASSWORD: "/forgot-password",
  INCIDENT_DETAIL: (incidentKey) => `/incidents/${incidentKey}`,
  INCIDENT_AUDIT: (incidentKey) => `/incidents/${incidentKey}/audit`,
}

// Query-param values for the manager Prompt Profiles settings tabs.
export const SETTINGS_TABS = {
  UPDATE_PROMPTS: "update-prompts",
  PROMPT_CONFIG: "prompt-config",
  BOT_PROFILES: "bot-profiles",
  VOICE_COPILOT: "voice-copilot",
  REPLANE: "replane",
}

export const API_PATHS = {
  COMPANY_DETAIL_FORM: "data/company-detail-form.json",
  // GAME_SCENARIO_FORM: "data/game-scenario-detail-form_v2.json",
  GAME_SCENARIO_FORM: "data/game-scenario-detail-form_test.json",
  RENDER_SETTINGS_DOCUMENT: "/settings/document/render",
  RENDER_REPORT_DOCUMENT: "/reports/document/render",
  UPLOAD_ORG_LOGO: "/org/logo/upload",
  INCIDENTS: "incidents",
  IMPORT_SCENARIO_FROM_FILE: "/importDataFromFile",
  // Shared integrations service (Jira / ServiceNow ticketing), reached through Hasura actions.
  INTEGRATIONS_OVERVIEW: "/integrations/overview",
  INTEGRATIONS_TEST_ACCOUNT: "/integrations/account/test",
  INTEGRATIONS_SAVE_ACCOUNT: "/integrations/account/save",
  INTEGRATIONS_CHECK_ACCOUNT: "/integrations/account/check",
  INTEGRATIONS_SET_ACCOUNT_ENABLED: "/integrations/account/enabled",
  INTEGRATIONS_DELETE_ACCOUNT: "/integrations/account/delete",
  INTEGRATIONS_GET_ALLOWED_TOOLS: "/integrations/allowed-tools/get",
  INTEGRATIONS_SET_ALLOWED_TOOLS: "/integrations/allowed-tools/set",
  // Every report's ticket (input `reportId`); the REST paths kept their first, Gap Analysis names.
  REPORT_TICKET_STATUS: "/reports/gap-analysis/ticket/status",
  REPORT_TICKET: "/reports/gap-analysis/ticket",
}

// Incident enums + limits
export const ROLE_HINT_OPTIONS = [
  { value: "ciso", label: "CISO" },
  { value: "irLead", label: "IR Lead" },
  { value: "socAnalyst", label: "SOC Analyst" },
  { value: "comms", label: "Communications" },
  { value: "legal", label: "Legal" },
  { value: "exec", label: "Executive" },
  { value: "other", label: "Other" },
]

// Pretty label for a member roleHint: known enum values map to their
// ROLE_HINT_OPTIONS label ("socAnalyst" → "SOC Analyst"); free-text roles
// ("Forensics Lead") render verbatim.
export const roleHintLabel = (roleHint) => {
  if (!roleHint) return ""
  const match = ROLE_HINT_OPTIONS.find((opt) => opt.value === roleHint)
  return match ? match.label : String(roleHint)
}

export const SEVERITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "med", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
]

export const INCIDENT_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024
export const INCIDENT_ATTACHMENT_MAX_COUNT = 5
export const INCIDENT_RUNBOOK_MAX_PLAINTEXT = 32 * 1024

// ── M4.2: incident status lifecycle ─────────────────────────────────────────
// 4-state lifecycle: active → mitigated → resolved → closed. Legacy 'archived'
// docs are treated as 'closed' everywhere client-side (see
// normalizeIncidentStatus below); 'archived' is never offered as a target.
export const INCIDENT_STATUS = {
  ACTIVE: "active",
  MITIGATED: "mitigated",
  RESOLVED: "resolved",
  CLOSED: "closed",
}

// from-status → allowed next statuses. Keep in lockstep with the server's
// STATUS_TRANSITIONS in chaos-player-poc/server/utils/incidentUtils.js — the
// server is authoritative (403s anything else); this map only shapes the
// status menu. Reopening from closed additionally requires sponsor or manager
// (enforced at the call site — the map itself is role-agnostic).
export const INCIDENT_STATUS_TRANSITIONS = {
  [INCIDENT_STATUS.ACTIVE]: [INCIDENT_STATUS.MITIGATED, INCIDENT_STATUS.RESOLVED, INCIDENT_STATUS.CLOSED],
  [INCIDENT_STATUS.MITIGATED]: [INCIDENT_STATUS.ACTIVE, INCIDENT_STATUS.RESOLVED, INCIDENT_STATUS.CLOSED],
  [INCIDENT_STATUS.RESOLVED]: [INCIDENT_STATUS.ACTIVE, INCIDENT_STATUS.MITIGATED, INCIDENT_STATUS.CLOSED],
  [INCIDENT_STATUS.CLOSED]: [INCIDENT_STATUS.ACTIVE, INCIDENT_STATUS.RESOLVED],
}

// Display metadata per lifecycle state — label for chips/menus, one-line
// description for the transition menu's secondary text.
export const INCIDENT_STATUS_META = {
  [INCIDENT_STATUS.ACTIVE]: { label: "Active", description: "Incident is ongoing." },
  [INCIDENT_STATUS.MITIGATED]: { label: "Mitigated", description: "Containment confirmed; recovery underway." },
  [INCIDENT_STATUS.RESOLVED]: { label: "Resolved", description: "Fully resolved; pending closure review." },
  [INCIDENT_STATUS.CLOSED]: { label: "Closed", description: "Incident record sealed. Read-only." },
}

// Missing/null → 'active' (M1 docs predate the status field); legacy
// 'archived' → 'closed'. Mirrors the server helper of the same name.
export const normalizeIncidentStatus = (status) => {
  if (status === undefined || status === null || status === "") return INCIDENT_STATUS.ACTIVE
  const normalized = String(status).toLowerCase()
  return normalized === "archived" ? INCIDENT_STATUS.CLOSED : normalized
}

// Remote Admin Console URL
export const ADMIN_BASE_URL = `${window.__RUNTIME_CONFIG__.REACT_APP_ADMIN}`

// Client Base URL
export const CLIENT_BASE_URL = `${window.__RUNTIME_CONFIG__.REACT_APP_ORIGIN}`

// Client Base Path - The base path where the app is served (e.g., "/client")
export const CLIENT_BASE_PATH = process.env.PUBLIC_URL ?? "/"

// Resolve a public/ asset against the app's base path so it loads whether the
// app is served at "/" (local dev) or "/client" (deployed). A bare "/images/…"
// string ignores the base path and 404s under /client. Normalize a trailing slash on the base
// and a leading slash on the relative path so a PUBLIC_URL like "/client/" doesn't produce "//".
export const publicAsset = (relPath) => {
  const base = CLIENT_BASE_PATH === "/" ? "" : CLIENT_BASE_PATH.replace(/\/+$/, "")
  return `${base}/${String(relPath).replace(/^\/+/, "")}`
}

export const DEFAULT_LOGO = publicAsset("images/logo_placeholder.png")
// The wordmark the Keycloak login theme serves, byte-identical, so a player cannot tell
// our email-code screen from the identity provider's own sign-in page. The placeholder
// above is the mark only (no lettering) and reads as a different product beside it.
export const LOGIN_WORDMARK = publicAsset("images/login-wordmark.png")
export const DEFAULT_LOGO_BLACK = publicAsset("images/logo_placeholder_black.png")

export const HEADER_NAV_ICONS = {
  GAME_VIEW: "GAME_VIEW",
}

export const PAGE_SOURCE = {
  GAME_LIST: "game-list",
  NEW_GAME: "new-game",
  DASBOARD: "dashboard",
  GROUP_DASHBOARD: "group_dasboard",
  DETAILS: "details",
}

export const PRINTABLE_REPORT_TYPE = {
  GENERATED_REPORT: "generated_report",
  MODIFIED_REPORT: "modified_report",
}

export const MODALVIEW_CONTAINER = "app-modal"

export const APP_MODAL_TYPES = {
  SUCCESS: "success",
  INFO: "info",
  ERROR: "error",
  ATTACHMENT: "attachment",
  FORM: "form",
  GAMEPLAY_HELP_SCREENS: "gameplay_help",
  COMPONENT: "component",
}

export const ATTACHMENT_TYPES = {
  IMAGE: "image",
}

export const USER_ROLES = {
  ADMIN: "admin",
  PLAYER: "player",
  SPECTATOR: "spectator",
}

export const FORM_FIELD_TYPES = {
  TEXT: "text",
  TEXTAREA: "textarea",
  DROPDOWN: "dropdown",
  MULTISELECT: "multiselect",
  RADIO: "radio",
  CHECKBOX: "checkbox",
}

export const TABLE_SORT_ORDER = {
  NONE: "none",
  ASC: "asc",
  DESC: "desc",
}

export const TABLE_GAME_FILTERS = {
  GAMES_STATUS: "GAMES_STATUS",
  TIME: "TIME",
  GROUP_BY: "GROUP_BY",
  TIME_SORT: "TIME_SORT",
  ORGANIZATION: "ORGANIZATION",
  ARCHIVED: "ARCHIVED",
}

export const TABLE_GAME_STATUS_FILTER = {
  ALL: "All",
  ONGOING: "Ongoing",
  FINISHED: "Finished",
  PAUSED: "Paused",
  SCHEDULED: "Scheduled",
}

export const TABLE_GAME_ARCHIVED_FILTER = {
  SHOW: "Show",
  HIDE: "Hide",
}

export const TABLE_GAME_TIME_FILTER = {
  ALL: "All",
  LAST_WEEK: "LAST_WEEK",
  LAST_MONTH: "LAST_MONTH",
  LAST_YEAR: "LAST_YEAR",
  YEAR_TO_DATE: "YEAR_TO_DATE",
}

export const TABLE_GAME_GROUP_BY_FILTER = {
  NONE: "",
  GAME_SCENARIO: "GAME_SCENARIO",
  GAME_STATUS: "GAME_STATUS",
}

export const TABLE_GAME_SCHEDULE_ACTION = {
  RESTART: "Restart",
  DUPLICATE: "Duplicate",
  EDIT: "Edit/Reschedule",
}

export const TABLE_GAME_USER_ACTION = {
  VIEW_GAME: "View Exercise",
  VIEW_DASHBOARD: "View Dashboard",
  VIEW_REPORT: "View Report",
}

export const TABLE_GAME_ADMIN_ACTION = {
  ADD_USERS: "Add Users",
  DELETE_GAME: "Delete Exercise",
  ARCHIVE_GAME: "Archive Exercise",
  UNARCHIVE_GAME: "Unarchive Exercise",
  MARK_GAME_FINISHED: "Mark Exercise Finished",
  UNMARK_GAME_FINISHED: "Unmark Exercise Finished",
}

export const TABLE_GAME_HISTORY_ACTION = {
  COMPARE_RUNS: "Compare runs",
}

export const FINDINGS_SEVERITIES = Object.freeze({
  HIGHLY_CRITICAL: "HIGHLY_CRITICAL",
  CRITICAL: "CRITICAL",
  MODERATE: "MODERATE",
  GOOD: "GOOD",
})

export const PHASE_REPORT_PATH_STATUS = Object.freeze({
  SUCCESS: "success",
  FAIL: "fail",
  PARTIAL: "partial",
})

export const COMPLETED_ACTION_STATUS = Object.freeze({
  SKIPPED: "skipped",
  COMPLETED: "completed",
  DYNAMIC: "dynamic",
  // End-of-exercise reconciliation judged a team input as PARTIALLY accomplishing this expected
  // action — rendered "Partial" (not "Missed Correct Action"), scored at PARTIAL_CREDIT of a core
  // action in lockstep with the server (chaosPlayerUtils.scoreSummaryForComposedSession).
  PARTIAL: "partial",
})

export const COMPLETED_ACTION_RESULT = Object.freeze({
  GOLDEN: "golden",
  LANDMINE: "landmine",
  CORRECT: "correct",
  INCORRECT: "incorrect",
})

export const DASHBOARD_CARD_TYPES = Object.freeze({
  OVERALL_PERFORMANCE: "OVERALL_PERFORMANCE",
  HISTORICAL_PERFORMANCE: "HISTORICAL_PERFORMANCE",
  GAME_STATUS: "GAME_STATUS",
  INDIVIDUAL_PERFORMANCE: "INDIVIDUAL_PERFORMANCE",
  RECOMMENDATIONS: "RECOMMENDATIONS",
})

export const DASHBOARD_TABLE_FILTER_TYPES = {
  FILTER_PARTICIPANT: "FILTER_PARTICIPANT",
  FILTER_GAME_STATUS: "FILTER_GAME_STATUS",
  FILTER_GAME_STATUS_TABLE_COUNT: "FILTER_GAME_STATUS_TABLE_COUNT",
  FILTER_AVERAGE_TIME: "FILTER_AVERAGE_TIME",
  FILTER_OVERVIEW_ACTION: "FILTER_OVERVIEW_ACTION",
  FILTER_OVERVIEW_MILESTONE_TIME: "FILTER_OVERVIEW_MILESTONE_TIME",
}

export const DASHBOARD_PARTICIPANT_FILTER = {
  HIGHEST: "Highest",
  LOWEST: "Lowest",
}

export const DASHBOARD_TABLE_ACTION_TYPES = {
  VIEW: "VIEW",
  VIEW_WITH_LABEL: "VIEW_WITH_LABEL",
  CALLBACK_WITH_LABEL: "CALLBACK_WITH_LABEL",
}

export const DASHBOARD_OVERALL_SCORE_RANGES = Object.freeze({
  "0_19": "0_19",
  "20_39": "20_39",
  "40_59": "40_59",
  "60_79": "60_79",
  "80_100": "80_100",
})

export const DASHBOARD_OVERALL_SCORE_COLORS = Object.freeze({
  // colors: ["#FF3E3E", "#FF6B1A", "#F7CA17", "#27AE61", "#3598DB"],
  "0_19": "#FF3E3E",
  "20_39": "#FF6B1A",
  "40_59": "#F7CA17",
  "60_79": "#27AE61",
  "80_100": "#3598DB",
})

export const DASHBOARD_STATISTICS_COLORS = Object.freeze({
  LOW: alpha(darken("#FF3E3E", 0.5), 0.5),
  AVERAGE: alpha(darken("#FF6B1A", 0.5), 0.5),
  HIGH: alpha(darken("#27AE61", 0.5), 0.5),
})

export const GRADE = Object.freeze({
  A: "A",
  B: "B",
  C: "C",
  D: "D",
  F: "F",
})

export const letterGradeForScore = (score) => {
  if (score >= 80) {
    return GRADE.A
  } else if (score >= 60) {
    return GRADE.B
  } else if (score >= 40) {
    return GRADE.C
  } else if (score >= 20) {
    return GRADE.D
  }
  return GRADE.F
}

export const colorForScore = (score) => {
  if (score >= 80) {
    return DASHBOARD_OVERALL_SCORE_COLORS["80_100"]
  } else if (score >= 60) {
    return DASHBOARD_OVERALL_SCORE_COLORS["60_79"]
  } else if (score >= 40) {
    return DASHBOARD_OVERALL_SCORE_COLORS["40_59"]
  } else if (score >= 20) {
    return DASHBOARD_OVERALL_SCORE_COLORS["20_39"]
  }
  return DASHBOARD_OVERALL_SCORE_COLORS["0_19"]
}

export const SCORE_ALERT_TYPE = Object.freeze({
  INCORRECT_ACTIONS: "INCORRECT_ACTIONS",
  QUERY_RATIO: "QUERY_RATIO",
  DUPLICATE_QUERIES: "DUPLICATE_QUERIES",
})

export const SCORE_ALERT_ENABLED = Object.freeze({
  INCORRECT_ACTIONS: false, // replane: scoring_alerts.incorrect_actions_enabled
  QUERY_RATIO: true, // replane: scoring_alerts.query_ratio_enabled
  DUPLICATE_QUERIES: true, // replane: scoring_alerts.duplicate_queries_enabled
})

export const SCORE_ALERT_THRESHOLDS = Object.freeze({
  INCORRECT_ACTIONS: 1, // replane: scoring_alerts.incorrect_actions_threshold
  QUERY_RATIO: 3.0, // replane: scoring_alerts.query_ratio_threshold
  QUERY_RATIO_MIN_QUERIES: 5, // replane: scoring_alerts.query_ratio_min_queries
  DUPLICATE_QUERIES: 3, // replane: scoring_alerts.duplicate_queries_threshold
})

export const shouldShowScoreAlert = ({ alertType, value, value2 }) => {
  let showAlert = false
  switch (alertType) {
    case SCORE_ALERT_TYPE.INCORRECT_ACTIONS:
      showAlert = SCORE_ALERT_ENABLED.INCORRECT_ACTIONS ? value >= SCORE_ALERT_THRESHOLDS.INCORRECT_ACTIONS : false
      break
    case SCORE_ALERT_TYPE.QUERY_RATIO:
      showAlert = SCORE_ALERT_ENABLED.QUERY_RATIO
        ? value >= SCORE_ALERT_THRESHOLDS.QUERY_RATIO && value2 >= SCORE_ALERT_THRESHOLDS.QUERY_RATIO_MIN_QUERIES
        : false
      break
    case SCORE_ALERT_TYPE.DUPLICATE_QUERIES:
      showAlert = SCORE_ALERT_ENABLED.DUPLICATE_QUERIES ? value >= SCORE_ALERT_THRESHOLDS.DUPLICATE_QUERIES : false
      break

    default:
      break
  }
  return showAlert
}

export const SCORE_ALERT_ICONS = Object.freeze({
  INCORRECT_ACTIONS: "mdi-bomb",
  QUERY_RATIO: "mdi-bomb",
  DUPLICATE_QUERIES: "mdi-controller",
})

export const SCORE_ALERT_TOOLTIPS = Object.freeze({
  INCORRECT_ACTIONS: (incorrectActions) =>
    `${incorrectActions} incorrect ${incorrectActions > 1 ? "actions were" : "action was"} performed during the simulation.`,
  QUERY_RATIO: (queryRatio) => {
    return `Query Bombing? Excessive action queries were observed during the simulation, by a factor of ${queryRatio.toPrecision(3)} times the norm.`
  },
  DUPLICATE_QUERIES: (duplicates) => {
    return `Gaming the System? Action queries were reused ${duplicates} ${duplicates > 1 ? `times` : `time`} during the simulation which may be an indicator of fishing for answers.`
  },
})

// GROUP GAME DETAILS CONSTANTS
export const DETAILS_ACTIONS_FILTER = {
  SORT_ORDER: "SORT_ORDER",
  DEPARTMENT: "DEPARTMENT",
}

export const DETAILS_INDIVIDUAL_PERFORMANCE_FILTER = {
  AVG_SORT_ORDER: "AVG_SORT_ORDER",
  DEPARTMENT: "DEPARTMENT",
  MIN_SCORE: "MIN_SCORE",
  MAX_SCORE: "MAX_SCORE",
}

export const DETAILS_OVERALL_RECOMMENDATIONS_FILTERS = {
  DEPARTMENT: "DEPARTMENT",
  AFFECTED_PARTICIPANTS: "AFFECTED_PARTICIPANTS",
  FINDING_SEVERITY: "FINDING_SEVERITY",
}

export const AVG_SORT_ORDER = {
  HIGHER_THAN_AVERAGE: "higher",
  LOWER_THAN_AVERAGE: "lower",
  DEFAULT: "none",
}

export const SORT_ORDER = {
  HIGHEST: "highest",
  LOWEST: "lowest",
}

export const DEPARTMENT_FILTER = {
  ALL: "All",
}

export const SCORE_FILTER = {
  ZERO: "ZERO",
  TWENTY: "TWENTY",
  FORTY: "FORTY",
  SIXTY: "SIXTY",
  EIGHTY: "EIGHTY",
  ONE_HUNDRED: "ONE_HUNDRED",
}

export const AFFECTED_PARTICIPANT_FILTER = {
  HIGHEST: "Highest",
  LOWEST: "Lowest",
}

export const SEVERITY_FILTER = {
  HIGHEST: "Highest",
  LOWEST: "Lowest",
}

export const SCENARIO_LIST_FILTERS = {
  SCENARIO_TYPE: "SCENARIO_TYPE",
}

export const SCENARIO_TYPES = {
  IMPORT: "IMPORT",
  CUSTOMIZED: "CUSTOMIZED",
  GENERATED: "GENERATED",
}

export const SCENARIO_TYPE_DISPLAY_TEXT = {
  IMPORT: "Standard",
  CUSTOMIZED: "Customized",
  GENERATED: "Generated",
}

export const SCENARIO_CARD_MENU_ITEM_IDS = {
  CUSTOMIZE: "Customize",
  DOWNLOAD: "Download",
  DELETE: "Delete",
  VIEW_DETAILS: "View Details",
  EDIT: "Edit",
  CONFIGURE: "Configure",
  ARCHIVE: "Archive",
}

export const SCENARIO_TYPE_FILTER = {
  ALL: "All",
  ...SCENARIO_TYPES,
}

export const TABLE_USER_PROFILE_LIMIT = 3 // replane: frontend / ui.table_user_profile_limit

export const MAX_INPUT_LIMIT = 4096 // replane: frontend / validation.max_text_entry_length

export const MAX_INPUT_LIMIT_LARGE = 128000 // replane: frontend / validation.max_ai_long_text_length

export const MAX_PRIVATE_INPUT_LIMIT = 131072 // replane: frontend / validation.max_private_text_entry_length

export const DATE_FORMAT = "MM-DD-YYYY" // replane: frontend / ui.date_format

export const DATE_FORMAT_SHORT = "MM-DD-YY" // replane: frontend / ui.date_format_short

export const TIME_FORMAT = "h:mm a" // replane: frontend / ui.time_format

export const PRINTING_TIMEOUT = 1000 // replane: frontend / ui.printing_timeout_ms

export const BUILDER_ACTION_TYPE = Object.freeze({
  CORRECT: "Correct",
  INCORRECT: "Incorrect",
  NEUTRAL: "Neutral",
})

// New Game Settings
// Default = the platform gameplay chain HEAD (Bedrock Sonnet 5), in lockstep with the server's
// MODEL_FALLBACK_CHAIN_DEFAULT: a defaulted exercise rides the resilient chain exactly as before,
// while an explicit "Choose an AI Model" pick is now honored verbatim (server chain fix 2026-09-03).
export const defaultSessionNLPModel = "us.anthropic.claude-sonnet-5" // replane: frontend / ai.nlp_model

export const nlpModelTypeLocal = "local"

export const defaultSessionNLPModelPromptMatchedActionsPrescreen = "" // Empty -> Use default
export const defaultSessionNLPModelPromptMatchedActionsPrescreenDisplayName = "gpt-5-2025-08-07" // replane: gameplay / ai.prescreen_model_display_name

export const SIDEBAR_WIDTH = 240 // replane: frontend / ui.sidebar_width
export const SIDEBAR_WIDTH_COLLAPSED = 64 // replane: frontend / ui.sidebar_width_collapsed

export const defaultSpectatorTitle = "Spectator" // replane: frontend / ui.default_spectator_title

export const LENGTH_VALIDATION_TYPES = {
  STANDARD_LENGTH: 255,
  AI_SHORT_TEXT_LENGTH: 4000, // replane: frontend / validation.max_ai_short_text_length
  AI_LONG_TEXT_LENGTH: 128000, // replane: frontend / validation.max_ai_long_text_length
}

export const PATTERN_VALIDATION_TYPES = {
  // # % = ~ ^ { } | added 2026-09-03: natural in scenario-edit instructions (markdown
  // headers, percentages, spreadsheet refs, key=value). < and > stay EXCLUDED - they
  // collide with the server's <token> substitution delimiters in prompts.
  SAFE_TEXT: /^[-a-zA-ZÀ-ÖØ-öø-ÿ0-9 _\\↵\-–—@+.,'‘’"“”:;?/!&()\$¥€£#%=~^{}|\r\n\t\*·•\[\]]+$/,
  // ONE charset for scenario display names AND exercise/group names (2026-09-03 unification).
  // Same set as SAFE_TEXT minus characters that are a transport/security problem in a name:
  //   /  — names travel as URL path segments; %2F is re-decoded and re-split by some path
  //        parsers (nginx, Hasura), breaking routing
  //   \  — path-normalization pitfalls in the same parsers
  //   < > — collide with the server's <token> substitution delimiters and HTML
  //   newlines/tabs — names are single-line
  // Everything else (including # % & = ? ;) is safe: all API calls RFC3986-encode the
  // segment, and the server looks names up via bind parameters (no SQL/AQL injection path).
  // Server lockstep: validAPINodeEntryMatchRegex in chaos-player-poc server/server.js.
  SAFE_NAME: /^[-a-zA-ZÀ-ÖØ-öø-ÿ0-9 _–—@+.,'‘’"“”:;?!&()$¥€£#%=~^{}|*·•[\]]+$/,
}

// File Upload Constants
export const SUPPORTED_FILE_MEDIA_TYPES = Object.freeze([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
  "image/png",
  "image/jpeg",
  "image/jpg",
  "text/plain",
])

export const SUPPORTED_FILE_TYPES_DISPLAY = "PDF, DOCX, PNG, JPG, JPEG, TXT"

export const FILE_UPLOAD_ACCEPT = ".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt"
// Supporting docs ground the AI security analysis, which can only consume PDF,
// images, and plain text — Word docs were accepted but silently dropped at
// generation time, so the picker no longer offers them here.
export const SUPPORTING_DOCS_ACCEPT = ".pdf,.png,.jpg,.jpeg,.txt"

// Scenario media (builder artifacts). Video is attached by URL and rendered as a <video> tag in the
// scene text; the player sanitises that HTML with CTDOMPurify, which permits video/source by default.
export const SUPPORTED_VIDEO_MEDIA_TYPES = Object.freeze(["video/mp4", "video/webm", "video/ogg", "video/quicktime"])

export const SUPPORTED_VIDEO_TYPES_DISPLAY = "MP4, WEBM, OGG, MOV"

export const VIDEO_UPLOAD_ACCEPT = ".mp4,.webm,.ogg,.mov"

// Matched against a URL pathname, so query strings and fragments do not defeat it.
export const VIDEO_URL_EXTENSIONS = /\.(mp4|webm|ogg|mov)$/i

// Matched against a URL pathname, like the video list above.
export const IMAGE_URL_EXTENSIONS = /\.(png|jpe?g|gif|webp|avif|bmp)$/i
